import React, { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import {
    Alert,
    Box,
    Grid,
    MenuItem,
    Slider,
    TextField,
    Typography,
} from '@mui/material';
import { LoadingButton, Button } from '@apigrowfinance/pbolt-button';
import CurrencyInput from '@apigrowfinance/pbolt-currency-input';
import { PDialog, PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
// eslint-disable-next-line import/no-unresolved
import { Notify } from 'notify/services/Notify';
import { Controller, useForm } from 'react-hook-form';
import { RiInformationLine } from 'react-icons/ri';
import { useIntl, FormattedMessage } from 'react-intl';

import { ICreateDisputeInputs } from '@clients/disputesClient';
import { TransactionItem } from '@clients/transactionsClient';
import constructDataToSend from '@components/transactions/reportDisputeDialog/constructDataToSend';
import getModalityValues from '@components/transactions/reportDisputeDialog/getModalityValues';
import reportDisputeDialogSchema from '@components/transactions/reportDisputeDialog/reportDisputeDialogSchema';
import { useAccount } from '@contexts/AccountProvider';
import { useOperatorSession } from '@contexts/OperatorSeesionProvider';
import { VisaDisputeReasons } from '@datasets/DisputeReasons';
import {
    VisaModalityOptions,
    MastercardModalityOptions,
    VisaListOfModalityOptionsRequiringReasons,
    ModalityOption,
} from '@datasets/Modality';
import usePostCreateDispute from '@hooks/queries/usePostCreateDispute';
import createDisputeValidation from '@messages/createDisputeValidation';
import ModalityMessages from '@messages/modality';
import visaDisputeReasonsMessages from '@messages/visaDisputeReasons';
import TransactionItemPanel from '@pages/transactions/TransactionItem';
import getEnumTranslation from '@utils/getEnumTranslation';

interface ReportDisputeDialogProps {
    open: boolean;
    closeCallback: () => void;
    transaction: TransactionItem;
}

const ReportDisputeDialog = ({
    open,
    closeCallback,
    transaction,
}: ReportDisputeDialogProps) => {
    const transactionAmount =
        transaction?.amount?.find(
            (amountObject) => amountObject.type === 'PRINCIPAL',
        )?.value || 0;
    const schema = reportDisputeDialogSchema(transaction);
    const { account, program } = useAccount();
    const { theme } = useTheme();
    const { protocol } = useOperatorSession();
    const { formatMessage } = useIntl();

    const [showReasons, setShowReasons] = React.useState(false);
    const notify = Notify.getInstance();

    const formDefaults: ICreateDisputeInputs = useMemo(() => {
        return {
            modality: '',
            dispute_due_to: '',
            amount: transactionAmount,
            arn: '',
            comment: '',
        };
    }, [transactionAmount]);

    const {
        handleSubmit,
        control,
        formState: { isDirty, errors, dirtyFields },
        reset,
    } = useForm<ICreateDisputeInputs>({
        resolver: yupResolver(schema),
        defaultValues: formDefaults,
    });
    const { isLoading: isSubmitting, mutate } = usePostCreateDispute(
        account?.account_id,
        () => {
            notify.addNotification(
                formatMessage({
                    id: 'ReportDisputeDialog.success',
                    defaultMessage: 'Dispute registered',
                }),
            );
            closeCallback();
        },
    );

    // Once transactions are loaded we reset the form so transaction data is used for the defaults
    useEffect(() => {
        reset(formDefaults);
    }, [formDefaults, reset, transaction]);

    const checkAndSetShowReasons = (modality: string) => {
        const code = getModalityValues(modality, programModalityReasons);
        const reasonRequired =
            VisaListOfModalityOptionsRequiringReasons.includes(
                code.split('.')[0],
            );
        setShowReasons(reasonRequired && program?.brand === 'VISA');
    };
    const programModalityReasons: ModalityOption[] =
        program?.brand === 'VISA'
            ? VisaModalityOptions
            : MastercardModalityOptions;

    const modalityReasons = programModalityReasons.map((modality) => (
        <MenuItem value={modality.ID} key={modality.ID}>
            {getEnumTranslation(modality.ID, {
                messages: ModalityMessages,
            })}
        </MenuItem>
    ));

    const onSubmit = (data: ICreateDisputeInputs) => {
        const dataToSend = constructDataToSend({
            data,
            transaction,
            transactionAmount,
            protocol,
            programModalityReasons,
        });
        mutate(dataToSend);
    };
    return (
        <PDialog
            dialogSubtitle={
                <FormattedMessage
                    id="ReportDisputeDialog.dialogSubtitle"
                    defaultMessage="To report a problem with this transaction fill in the fields below then click report problem. "
                />
            }
            dialogTitle={
                <FormattedMessage
                    id="ReportDisputeDialog.title"
                    defaultMessage="Report a problem"
                />
            }
            onClose={closeCallback}
            open={open}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <PDialogContent>
                    <Box
                        sx={{
                            backgroundColor: 'grey.50',
                            borderRadius: 1,
                            mb: 3.75,
                        }}>
                        <TransactionItemPanel
                            transaction={transaction}
                            clickable={false}
                        />
                    </Box>

                    <Controller
                        name="modality"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                error={!!errors.modality}
                                helperText={
                                    !!errors.modality &&
                                    getEnumTranslation(
                                        errors.modality?.message,
                                        {
                                            messages: createDisputeValidation,
                                        },
                                    )
                                }
                                onChange={(e) => {
                                    checkAndSetShowReasons(e.target.value);
                                    field.onChange(e.target.value);
                                }}
                                data-testid="type-textfield"
                                sx={{ mb: showReasons ? 3.75 : 3.5 }}
                                label={
                                    <FormattedMessage
                                        id="ReportDisputeDialog.selectType"
                                        defaultMessage="Select type"
                                    />
                                }
                                inputProps={{ 'data-testid': 'type' }}
                                select
                                fullWidth>
                                {modalityReasons}
                            </TextField>
                        )}
                    />
                    {showReasons && (
                        <Controller
                            name="dispute_due_to"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    error={!!errors.dispute_due_to}
                                    helperText={
                                        !!errors.dispute_due_to &&
                                        getEnumTranslation(
                                            errors.dispute_due_to?.message,
                                            {
                                                messages:
                                                    createDisputeValidation,
                                            },
                                        )
                                    }
                                    data-testid="reason-textfield"
                                    sx={{ mb: 3.5 }}
                                    label={
                                        <FormattedMessage
                                            id="ReportDisputeDialog.selectReason"
                                            defaultMessage="Select reason"
                                        />
                                    }
                                    inputProps={{ 'data-testid': 'reason' }}
                                    select
                                    fullWidth>
                                    {VisaDisputeReasons.map((reason) => (
                                        <MenuItem
                                            value={reason.value}
                                            key={reason.ID}>
                                            {getEnumTranslation(reason.ID, {
                                                messages:
                                                    visaDisputeReasonsMessages,
                                            })}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />
                    )}
                    <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                            <Box sx={{ width: '100%' }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid
                                        item
                                        xs={9}
                                        sx={{
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            mb: 4,
                                        }}>
                                        <Typography
                                            mb={0}
                                            id="input-slider"
                                            gutterBottom
                                            variant="body2">
                                            <FormattedMessage
                                                id="ReportDisputeDialog.disputeAmount"
                                                defaultMessage="Dispute amount"
                                            />
                                        </Typography>
                                        <Slider
                                            data-testid="amount-slider"
                                            {...field}
                                            min={0}
                                            max={transactionAmount}
                                            aria-labelledby="input-slider"
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <CurrencyInput
                                            {...field}
                                            data-testid="amount-input"
                                            currency={
                                                program?.currency_numeric_code
                                            }
                                            inputProps={{
                                                'aria-labelledby':
                                                    'input-slider',
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    />
                    {!transaction?.authorization?.id && (
                        <Controller
                            name="arn"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    sx={{ mb: 3.75 }}
                                    label={
                                        <FormattedMessage
                                            id="ReportDisputeDialog.reference"
                                            defaultMessage="Acquirer reference number"
                                        />
                                    }
                                    {...field}
                                    error={!!errors.arn}
                                    helperText={
                                        !!errors.arn &&
                                        getEnumTranslation(
                                            errors.arn?.message,
                                            {
                                                messages:
                                                    createDisputeValidation,
                                            },
                                        )
                                    }
                                    value={field.value || ''}
                                    fullWidth
                                />
                            )}
                        />
                    )}
                    <Controller
                        name="comment"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                label={
                                    <FormattedMessage
                                        id="ReportDisputeDialog.comment"
                                        defaultMessage="Leave a comment"
                                    />
                                }
                                {...field}
                                error={!!errors.comment}
                                helperText={
                                    !!errors.comment &&
                                    getEnumTranslation(
                                        errors.comment?.message,
                                        {
                                            messages: createDisputeValidation,
                                        },
                                    )
                                }
                                value={field.value || ''}
                                rows={4}
                                multiline
                                fullWidth
                            />
                        )}
                    />
                    {!!dirtyFields?.modality && (
                        <Alert
                            severity="info"
                            icon={
                                <RiInformationLine
                                    color={theme?.palette?.info?.main}
                                />
                            }
                            sx={{
                                fontWeight: '700',
                                fontSize: '0.75',
                                backgroundColor: 'info.light',
                                color: 'info.main',
                            }}>
                            <FormattedMessage
                                id="ReportDisputeDialog.responseNotice"
                                defaultMessage="We’ll send the details to our disputes back office team where it will be handled."
                            />
                        </Alert>
                    )}
                </PDialogContent>
                <PDialogAction>
                    <Button onClick={closeCallback}>
                        {getEnumTranslation('cancel')}
                    </Button>
                    <LoadingButton
                        disabled={!isDirty || isSubmitting}
                        isLoading={isSubmitting}>
                        <FormattedMessage
                            id="ReportDisputeDialog.reportProblem"
                            defaultMessage="Report problem"
                        />
                    </LoadingButton>
                </PDialogAction>
            </form>
        </PDialog>
    );
};

export default ReportDisputeDialog;
